<template>
  <div>
    <button
      v-show="!open"
      type="button"
      class="sue-fab"
      @click="$emit('open')"
    >
      <span class="fab-av">Sue</span>
      <span class="fab-txt">
        <b>Chat with Sue</b>
        <i>Your course assistant</i>
      </span>
    </button>

    <div v-if="open" class="sue-panel">
      <div class="sp-head">
        <div>
          <b>Sue</b>
          <span>Your course assistant</span>
        </div>
        <button type="button" class="sp-close" @click="$emit('close')">
          ✕
        </button>
      </div>
      <div class="sp-body">
        <div v-for="(m, i) in messages" :key="i">
          <div v-if="m.role === 'sue'" class="msg msg-sue">
            <div class="bubble">{{ m.text }}</div>
            <div v-if="m.chips && m.chips.length" class="chips">
              <button
                v-for="(c, j) in m.chips"
                :key="j"
                type="button"
                class="chip"
                @click="chipAction(c)"
              >
                {{ c.label }}
              </button>
            </div>
          </div>
          <div v-else-if="m.role === 'user'" class="msg msg-user">
            <div class="bubble">{{ m.text }}</div>
          </div>
          <div v-else-if="m.role === 'handoff'" class="msg msg-sue">
            <div class="bubble">
              <b>Here's a human for you 👇</b>
              <div
                v-for="t in trainers"
                :key="t.id"
                class="hand-row"
              >
                <b>{{ t.name }}</b>
                <span>{{ t.role }}</span>
                <a :href="'mailto:' + t.email">{{ t.email }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!handed" class="sp-quick">
        <button
          v-for="q in quickies"
          :key="q"
          type="button"
          class="chip"
          @click="send(q)"
        >
          {{ q }}
        </button>
      </div>
      <div class="sp-input">
        <input
          v-model="draft"
          type="text"
          placeholder="Ask Sue anything…"
          @keydown.enter="send(draft)"
        />
        <button type="button" class="send-btn" @click="send(draft)">
          Send
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SueChat',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    kb: {
      type: Array,
      default: () => []
    },
    trainers: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      draft: '',
      handed: false,
      quickies: ['How do I prepare?', 'When is the exam?', 'What payment terms apply?'],
      messages: []
    }
  },
  watch: {
    open (val) {
      if (val && this.messages.length === 0) {
        const first = this.$store.state.user.first_name || ''
        this.messages.push({
          role: 'sue',
          text: `Hi ${first} — I'm Sue, your course assistant. Ask me anything about your certification — prep, the days, materials, payment or your trainers.`,
          chips: []
        })
      }
    }
  },
  methods: {
    send (text) {
      const q = (text || '').trim()
      if (!q) return
      this.draft = ''
      this.messages.push({ role: 'user', text: q })
      const lower = q.toLowerCase()
      const hit = this.kb.find((e) =>
        e.keys.some((k) => lower.includes(k))
      )
      if (hit) {
        this.messages.push({
          role: 'sue',
          text: hit.answer,
          chips: hit.chips
        })
      } else {
        this.messages.push({
          role: 'sue',
          text: "I'm not sure about that one — the quickest route is a human. Try 'Talk to a human', or email info@gdq.se and we'll get back to you.",
          chips: [{ label: 'Talk to a human', target: 'human' }]
        })
      }
    },
    chipAction (chip) {
      if (chip.target === 'human') {
        if (!this.handed) {
          this.handed = true
          this.messages.push({ role: 'handoff' })
        }
        return
      }
      this.messages.push({
        role: 'user',
        text: chip.label
      })
      this.$emit('action', chip.target)
    }
  }
}
</script>

<style scoped>
.sue-fab {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--line);
  background: var(--white);
  border-radius: 999px;
  padding: 8px 16px 8px 8px;
  cursor: pointer;
  box-shadow: 0 16px 34px -18px rgba(13, 27, 42, 0.55);
  font-family: inherit;
}
.fab-av {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--berry), var(--coral));
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 0.8rem;
  font-weight: 800;
}
.fab-txt {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.25;
}
.fab-txt b {
  font-size: 0.86rem;
  color: var(--ink);
}
.fab-txt i {
  font-style: normal;
  font-size: 0.72rem;
  color: var(--ink-soft);
}
.sue-panel {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 61;
  width: min(380px, calc(100vw - 44px));
  height: min(540px, calc(100vh - 60px));
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: 0 30px 60px -25px rgba(13, 27, 42, 0.6);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, var(--teal-dk), var(--ink));
  color: #fff;
}
.sp-head b {
  display: block;
  font-size: 0.98rem;
}
.sp-head span {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}
.sp-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  padding: 4px;
}
.sp-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--paper);
}
.msg {
  display: flex;
  flex-direction: column;
}
.msg-sue {
  align-items: flex-start;
}
.msg-user {
  align-items: flex-end;
}
.bubble {
  max-width: 88%;
  padding: 10px 13px;
  border-radius: 14px;
  font-size: 0.88rem;
  line-height: 1.5;
}
.msg-sue .bubble {
  background: var(--white);
  border: 1px solid var(--line);
  border-bottom-left-radius: 4px;
}
.msg-user .bubble {
  background: var(--teal);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.chip {
  border: 1px solid rgba(0, 122, 143, 0.35);
  background: var(--white);
  color: var(--teal-dk);
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 5px 11px;
  cursor: pointer;
  font-family: inherit;
}
.chip:hover {
  background: rgba(0, 122, 143, 0.08);
}
.hand-row {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 8px;
}
.hand-row a {
  color: var(--teal-dk);
  font-size: 0.83rem;
}
.sp-quick {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 10px 16px 0;
}
.sp-input {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--line);
  background: var(--white);
}
.sp-input input {
  flex: 1;
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 9px 14px;
  font-size: 0.88rem;
  font-family: inherit;
  outline: none;
}
.sp-input input:focus {
  border-color: var(--teal);
}
.send-btn {
  border: none;
  background: var(--teal);
  color: #fff;
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
</style>
